import { Dimensions } from '../../../core/types/dimensions.js';
import { HoneycombTileShape } from '../../types/honeycomb.js';
import { HiveTileBestFitProps } from '../../types/rendering.js';
import { assertTilesFittingInArea } from '../assert-tiles-fitting-in-area.js';
import { calculateGap } from '../calculate-gap.js';
import { calculateTileHeight } from '../calculate-tile-height.js';
import { calculateTileWidth } from '../calculate-tile-width.js';

const calculateVerticalBestFit = (
  shape: HoneycombTileShape,
  graphArea: Dimensions,
  totalElements: number,
) => {
  const { height: ctnHeight, width: ctnWidth } = graphArea;
  let bestFit: HiveTileBestFitProps = {
    tileWidth: 0,
    hiveHeight: 0,
    gap: 0,
  };
  let lowerBound = 1;
  let upperBound = ctnHeight * 2;

  while (lowerBound < upperBound) {
    const candidateHeight = Math.floor((upperBound + lowerBound) / 2);
    const candidateWidth = calculateTileWidth(candidateHeight, shape);
    const gap = calculateGap(candidateWidth, ctnWidth);

    const candidateWiderThanContainer = candidateWidth > ctnWidth;

    if (candidateWiderThanContainer) {
      upperBound = candidateHeight - 1;
      continue;
    }

    const { drawableElements, hiveHeight } = assertTilesFittingInArea(
      candidateWidth,
      candidateHeight,
      graphArea,
      shape,
      totalElements,
      gap,
    );

    const isBetterThanPrevious = candidateWidth > bestFit.tileWidth;
    const isGoodCandidate =
      drawableElements >= totalElements && hiveHeight <= ctnHeight;

    if (isGoodCandidate && isBetterThanPrevious) {
      bestFit = {
        tileWidth: candidateWidth,
        hiveHeight,
        gap,
      };
    }

    drawableElements > totalElements
      ? (lowerBound = candidateHeight + 1)
      : (upperBound = candidateHeight - 1);
  }

  return bestFit;
};

/**
 * Calculate which is the maximum width that a tile could have to fit the
 * `totalElements` within the `graphArea`. To check it, uses a binary search
 * from 1 to the width of the container checking on each iteration how many
 * elements could be drawn in the hive with this width. As the way of checkin
 * how many elements fit varies from shape to shape, we implemented a strategy.
 * @param shape -
 * @param graphArea -
 * @param totalElements -
 */
const calculateHorizontalBestFit = (
  shape: HoneycombTileShape,
  graphArea: Dimensions,
  totalElements: number,
) => {
  const { height: ctnHeight, width: ctnWidth } = graphArea;
  let bestFit: HiveTileBestFitProps = {
    tileWidth: 0,
    hiveHeight: 0,
    gap: 0,
  };
  let lowerBound = 1;
  let upperBound = ctnWidth * 2;

  while (lowerBound < upperBound) {
    const candidateWidth = Math.floor((upperBound + lowerBound) / 2);
    const candidateHeight = calculateTileHeight(shape, candidateWidth);
    const gap = calculateGap(candidateWidth, ctnWidth);

    const candidateTallerThanContainer = candidateHeight > ctnHeight;

    if (candidateTallerThanContainer) {
      upperBound = candidateWidth - 1;
      continue;
    }

    const { drawableElements, hiveHeight } = assertTilesFittingInArea(
      candidateWidth,
      candidateHeight,
      graphArea,
      shape,
      totalElements,
      gap,
    );

    const isBetterThanPrevious = candidateWidth > bestFit.tileWidth;
    const isGoodCandidate =
      drawableElements >= totalElements && hiveHeight <= ctnHeight;

    if (isGoodCandidate && isBetterThanPrevious) {
      bestFit = {
        tileWidth: candidateWidth,
        hiveHeight,
        gap,
      };
    }

    drawableElements > totalElements
      ? (lowerBound = candidateWidth + 1)
      : (upperBound = candidateWidth - 1);
  }

  return bestFit;
};

export const calculateOptimalTileSize = (
  shape: HoneycombTileShape,
  graphArea: Dimensions,
  totalElements: number,
) => {
  const horizontalBestFit = calculateHorizontalBestFit(
    shape,
    graphArea,
    totalElements,
  );
  if (horizontalBestFit.hiveHeight < graphArea.height * 0.9) {
    const verticalBestFit = calculateVerticalBestFit(
      shape,
      graphArea,
      totalElements,
    );
    const verticalFitsBetter =
      verticalBestFit.tileWidth > horizontalBestFit.tileWidth;

    return verticalFitsBetter ? verticalBestFit : horizontalBestFit;
  }
  return horizontalBestFit;
};
